import {IUser} from '@src/@interfaces/services/Auth/IUser';
import {ObjectKeys} from '@src/@types/ObjectKeys';
import UserPreferencesRepository from './UserPreferencesRepository';
import app from '@src/helpers/app';
import {IUserPreferencesNamesResolver} from '@src/@interfaces/services/NameResolvers/IUserPreferencesNamesResolver';
import {UserPreferencesKeys} from '@src/@types/services/UserPreferencesNamesResolver';
import {IUserRoleModel} from '@src/@interfaces/models/User/IUserRoleModel';
import {IUserPreferenceModel} from '@src/@interfaces/models/User/IUserPreferenceModel';
import {IUserPreference} from '@src/@interfaces/services/Auth/IUserPreference';
import {IModelPropertyModel} from '@src/@interfaces/models/ModelProperty/IModelPropertyModel';

export default class User implements IUser {
    static SUPER_USER_ONLY_PERMISSION = 'super-admin-only';

    readonly id!: number;
    readonly name?: string;
    readonly email!: string;
    readonly creator_id?: number | null;
    readonly user_role_id?: number | null;
    readonly remover_id?: number | null;
    readonly is_super_user!: boolean;
    readonly is_online!: boolean;
    readonly is_in_production_department!: boolean;
    readonly full_name?: string | null;
    readonly phone?: string | null;
    readonly email_verified_at?: string | null;
    readonly created_at?: string | null;
    readonly updated_at?: string | null;
    readonly deleted_at?: string | null;
    readonly role?: IUserRoleModel | null;
    readonly preferences?: IUserPreferenceModel[];
    readonly preferencesRepository: UserPreferencesRepository;

    protected specificPermissionsMap: Record<string, string> = {};
    protected rawAttributes: ObjectKeys<IUser>;

    protected userPreferencesNamesResolver?: IUserPreferencesNamesResolver;

    protected receivedProperties: Record<string, IModelPropertyModel> = {};

    constructor(attributes: ObjectKeys<IUser>) {
        this.fill(this.rawAttributes = attributes);

        this.preferencesRepository = new UserPreferencesRepository(this.id, this.preferences ?? []);
    }

    resolveModelPreferenceName(model: string, key: UserPreferencesKeys): string {
        return this.namesResolver.resolveName(model, key);
    }

    preference(key: string): IUserPreference {
        return this.preferencesRepository.get(key);
    }

    getPreference(key: string): any | null {
        return this.preferencesRepository.getValue(key);
    }

    getModelPreferenceValue(model: string, key: UserPreferencesKeys): any | null {
        return this.getPreference(
            this.resolveModelPreferenceName(model, key)
        );
    }

    setModelPreferenceValue(model: string, key: UserPreferencesKeys, value: any): void {
        this.setPreference(
            this.resolveModelPreferenceName(model, key),
            value
        );
    }

    saveModelPreferenceValue(model: string, key: UserPreferencesKeys, value: any): Promise<boolean> {
        return this.preference(
            this.resolveModelPreferenceName(model, key)
        ).save({value});
    }

    setPreference(key: string, value: any): void {
        this.preference(key).fill({value});
    }

    async savePreference(key: string, value: any): Promise<boolean> {
        return this.preference(key).save({value});
    }

    fill(attributes: ObjectKeys<IUser>): IUser {
        Object.assign(this, attributes);
        this.buildSpecificPermissionsMap();

        return this;
    }

    can(permissions: string | string[]): boolean {
        if (this.is_super_user) {
            return true;
        }

        return (Array.isArray(permissions) ? permissions : [permissions])
            .every(permission => this.isAllowedPermission(permission));
    }

    cant(permissions: string | string[]): boolean {
        return !this.can(permissions);
    }

    cannot(permissions: string | string[]): boolean {
        return this.cant(permissions);
    }

    canAny(permissions: string | string[]): boolean {
        const list = Array.isArray(permissions) ? permissions : [permissions];

        return !list.length || list.some(permission => this.can(permission));
    }

    cannotAny(permissions: string | string[]): boolean {
        const list = Array.isArray(permissions) ? permissions : [permissions];

        return !list.length || list.some(permission => !this.can(permission));
    }

    toArray(): ObjectKeys<IUser> {
        return this.rawAttributes;
    }

    getProperties(key: string): Promise<IModelPropertyModel>;
    getProperties(keys: string[]): Promise<IModelPropertyModel[]>;
    getProperties(...keys: Array<string | string[]>): Promise<IModelPropertyModel | IModelPropertyModel[]> {
        const flattenKeys: string[] = [];
        keys.forEach(item => {
            if (Array.isArray(item)) {
                item.forEach(key => flattenKeys.push(key));
            } else {
                flattenKeys.push(item);
            }
        });

        // eslint-disable-next-line prefer-rest-params
        const singular = arguments.length === 1 && typeof arguments[0] === 'string';

        const result: IModelPropertyModel[] = [];
        const allInCache = flattenKeys.every(key => {
            if (key in this.receivedProperties) {
                result.push(this.receivedProperties[key]);

                return true;
            }

            return false;
        });

        if (allInCache) {
            return Promise.resolve(singular ? result[0] : result);
        }

        return app('http').route('me.get-properties', {}, {
            body: {keys: flattenKeys}
        }).then(value => {
            if (singular) {
                return value[0];
            }

            return value;
        });
    }

    clearPropertiesCache(): void {
        this.receivedProperties = {};
    }

    protected isSpecificPermission(permission: string): boolean {
        return permission in this.specificPermissionsMap;
    }

    protected resolveSpecificPermission(permission: string, defaultValue = false): boolean {
        if (this.specificPermissionsMap[permission] === User.SUPER_USER_ONLY_PERMISSION) {
            return this.is_super_user;
        }

        return defaultValue;
    }

    protected isAllowedPermission(permission: string): boolean {
        if (this.isSpecificPermission(permission)) {
            return this.resolveSpecificPermission(permission);
        }

        const permissionsContainer = this.role?.allowed_permissions ?? this.role?.all_permissions;
        if (!permissionsContainer) {
            return false;
        }

        return Boolean(permissionsContainer[permission]);
    }

    protected buildSpecificPermissionsMap(): void {
        this.specificPermissionsMap = {};
        for (const [key, value] of Object.entries(this.role?.specific_permissions ?? {})) {
            this.specificPermissionsMap[value] = key;
        }
    }

    protected get namesResolver(): IUserPreferencesNamesResolver {
        return this.userPreferencesNamesResolver ??= app('names-resolver').userPreferences;
    }
}
